/**
 * InspectionReportShareCard — Admin-only card on a job detail that mints,
 * copies, SMS-shares, and revokes a public tokenized link to the completed
 * inspection PDF. View counter shows if the recipient has opened it.
 *
 * The link points directly to the PDF (streamed inline) so iMessage / WhatsApp
 * / SMS / email all render a preview and open in the browser's PDF viewer —
 * no viewer page or login required.
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Link as LinkIcon, Copy, ArrowClockwise, Trash, Eye, PaperPlaneTilt,
  FilePdf, EnvelopeSimple,
} from "@phosphor-icons/react";

export default function InspectionReportShareCard({ jobId, job }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { setStats((await api.get(`/jobs/${jobId}/report-share-stats`)).data); }
    catch (_) { setStats({ has_link: false }); }
  };
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  // Always prefer the current browser origin over any backend-provided URL —
  // that way the link works even when APP_PUBLIC_URL is unset / misconfigured
  // on the server. The backend still returns `stats.path` as the source of
  // truth for the token path.
  const path = stats?.path || (stats?.token ? `/api/public/inspection-report/${stats.token}/pdf` : "");
  const url  = path ? `${window.location.origin}${path}` : "";

  const mint = async (rotate = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/report-share-link${rotate ? "?rotate=true" : ""}`);
      toast.success(rotate ? "Link rotated — old link is dead" : "Report link created");
      setStats(r.data);
      // Rebuild the URL from the browser origin so we don't depend on
      // backend APP_PUBLIC_URL being set correctly (production hit us on this).
      const nextPath = r.data.path || `/api/public/inspection-report/${r.data.token}/pdf`;
      const nextUrl  = `${window.location.origin}${nextPath}`;
      try {
        await navigator.clipboard.writeText(nextUrl);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
      } catch (_) {}
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not create link"));
    } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success("Report link copied");
    } catch (_) { toast.error("Copy failed — long-press to copy manually"); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke this report link? Anyone with it will lose access immediately.")) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${jobId}/report-share-link`);
      toast.success("Link revoked");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Revoke failed")); }
    finally { setBusy(false); }
  };
  const openPdf = () => { if (url) window.open(url, "_blank"); };

  const label = job?.is_turn
    ? "Turn Report"
    : String(job?.inspection_type || "").toLowerCase() === "periodic" ? "Periodic Inspection Report"
    : String(job?.inspection_type || "").toLowerCase() === "move_in"  ? "Move-In Report"
    : String(job?.inspection_type || "").toLowerCase() === "move_out" ? "Move-Out Report"
    : "Inspection Report";

  const addr = job?.property_address ? ` — ${job.property_address}${job.unit ? ` #${job.unit}` : ""}` : "";
  const smsText = `${label}${addr}${job?.work_order_number ? ` · WO ${job.work_order_number}` : ""}\n${url}`;
  const emailSubject = `${label}${addr}${job?.work_order_number ? ` · WO ${job.work_order_number}` : ""}`;
  const emailBody = `Attached inspection report:\n${url}\n\nSent from Field Radar`;

  return (
    <div className="mt-4 rounded-md border-2 border-fuchsia-200 bg-fuchsia-50/50 p-3" data-testid="report-share-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FilePdf weight="fill" size={18} className="text-fuchsia-700 shrink-0"/>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-widest text-fuchsia-800">Share Completed Report</div>
            <div className="text-[10px] text-slate-500">
              {stats?.has_link
                ? `${stats.view_count || 0} view${stats.view_count === 1 ? "" : "s"}${stats.last_viewed_at ? ` · last opened ${new Date(stats.last_viewed_at).toLocaleString()}` : " · not opened yet"}`
                : "No link yet — mint one to send owners, tenants, insurance, or claims adjusters"}
            </div>
          </div>
        </div>
        {stats?.has_link ? (
          <div className="flex items-center gap-1 flex-wrap">
            <button type="button" onClick={copy} data-testid="report-share-copy"
              className="h-8 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <Copy weight="bold" size={11}/> {copied ? "Copied" : "Copy"}
            </button>
            <a href={`sms:?&body=${encodeURIComponent(smsText)}`} data-testid="report-share-sms"
              className="h-8 px-3 bg-white border-2 border-fuchsia-300 text-fuchsia-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <PaperPlaneTilt weight="fill" size={11}/> SMS
            </a>
            <a href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
              data-testid="report-share-email"
              className="h-8 px-3 bg-white border-2 border-fuchsia-300 text-fuchsia-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <EnvelopeSimple weight="bold" size={11}/> Email
            </a>
            <button type="button" onClick={openPdf} data-testid="report-share-open"
              className="h-8 px-3 bg-white border-2 border-fuchsia-300 text-fuchsia-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <Eye weight="bold" size={11}/> Preview
            </button>
            <button type="button" onClick={() => mint(true)} disabled={busy}
              data-testid="report-share-rotate"
              title="Rotate the token — the old link stops working"
              className="h-8 px-2 bg-white border-2 border-slate-300 text-slate-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
              <ArrowClockwise weight="bold" size={11}/>
            </button>
            <button type="button" onClick={revoke} disabled={busy}
              data-testid="report-share-revoke"
              title="Kill this link"
              className="h-8 px-2 bg-white border-2 border-red-200 text-red-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
              <Trash weight="bold" size={11}/>
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => mint(false)} disabled={busy}
            data-testid="report-share-create"
            className="h-9 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
            <LinkIcon weight="bold" size={12}/> Create Report Link
          </button>
        )}
      </div>
      {stats?.has_link && url && (
        <div className="mt-2 text-[11px] font-mono text-slate-600 bg-white/70 border border-fuchsia-200 rounded px-2 py-1 truncate flex items-center gap-1"
          data-testid="report-share-url">
          <LinkIcon weight="bold" size={11} className="text-slate-400 shrink-0"/>
          <span className="truncate">{url}</span>
        </div>
      )}
    </div>
  );
}
